'use client';

import { useEffect } from 'react';
import Script from 'next/script';
import { usePathname } from 'next/navigation';

// Important: Declare FB_PIXEL_ID directly to avoid any issues with environment variables
const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID;

/**
 * Initialize analytics (Meta Pixel, GA, etc.)
 * This component should be included in the root layout or a shared layout component
 *
 * IMPORTANT: This component only initializes the pixel but DOES NOT track PageView events.
 * PageView events are tracked ONLY by the PageViewTracker component on the home page.
 */
export function AnalyticsInitializer() {
  const pathname = usePathname();
  useEffect(() => {
    // Log initialization
    console.log('[Analytics Init] Starting initialization');

    // Skip in SSR or if pixel ID is missing
    if (typeof window === 'undefined' || !FB_PIXEL_ID) {
      console.log('[Analytics Init] Cannot initialize - SSR or missing pixel ID');
      return;
    }

    // Check if already initialized
    if (window.fbq) {
      console.log('[Analytics Init] Facebook Pixel already initialized');
      return;
    }

    // Log the pixel ID to confirm it's loaded
    console.log(`[Analytics Init] Initializing with Pixel ID: ${FB_PIXEL_ID}`);
    try {
      // Add fbq to window
      window.fbq = function () {
        window.fbq.callMethod ? window.fbq.callMethod.apply(window.fbq, arguments) : window.fbq.queue.push(arguments);
      };

      // Initialize fbq
      if (!window._fbq) {
        window._fbq = window.fbq;
      }
      window.fbq.push = window.fbq;
      window.fbq.loaded = true;
      window.fbq.version = '2.0';
      window.fbq.queue = [];

      // Initialize pixel but DO NOT track PageView
      window.fbq('init', FB_PIXEL_ID);

      // Disable automatic config
      window.fbq('set', 'autoConfig', false, FB_PIXEL_ID);
      console.log('[Analytics Init] Facebook Pixel initialized');
    } catch (error) {
      console.error('[Analytics Init] Error initializing analytics:', error);
    }
  }, []);
  return <>
      {/* Meta Pixel Script with automatic PageView tracking DISABLED */}
      <Script id="fb-pixel" strategy="afterInteractive" dangerouslySetInnerHTML={{
      __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            
            // Initialize pixel WITHOUT automatic PageView tracking
            fbq('init', '${FB_PIXEL_ID}');
            
            // CRITICAL: Disable automatic tracking
            fbq('set', 'autoConfig', false, '${FB_PIXEL_ID}');
            
            console.log('[Analytics Init] Facebook Pixel script loaded');
          `
    }} onLoad={() => console.log('[Analytics Init] Facebook Pixel script loaded')} onError={e => console.error('[Analytics Init] Error loading Facebook Pixel:', e)} data-sentry-element="Script" data-sentry-source-file="init.tsx" />
    </>;
}